import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { decrypt, encrypt } from '../../../common/enc_dec';
import { certificatePerCourse_ } from '../../../slices/reportSlice';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import Loader from '../../../components/loader';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Swal from 'sweetalert2';
import { useModuleSettings } from '../../../components/moduleSettingsContext/ModuleSettingsContext';
import * as XLSX from 'xlsx';

const UsersCertificates = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id, total_certificates } = location?.state || {};
    const queryParams = new URLSearchParams(location.search);
    const _id = queryParams.get("id") ? decrypt(queryParams.get("id")) : null;
    const courseId = id || _id;

    const { certificatePerCourse, loading } = useSelector((state) => state.report);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const { filter, setFilter, page, setPage, limit, setLimit } = useModuleSettings();
    const [isExporting, setIsExporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (courseId) {
            dispatch(certificatePerCourse_({
                id: courseId,
                period: filter,
                startDate,
                endDate,
                page,
                limit,
                search: searchTerm
            }));
        }
    }, [dispatch, courseId, filter, page, limit, startDate, endDate, searchTerm]);

    const handleViewUser = (row) => {
        navigate(`/users/view?id=${encrypt(`${row.user_id}`)}`, { 
            state: { userData: row } 
        });
    };

    const handleViewCertificate = (row) => {
        if (row.certificate) {
            window.open(row.certificate, '_blank');
        } else {
            Swal.fire({
                icon: 'info',
                title: 'Certificate Not Available',
                text: 'Certificate is not available for this user.',
            });
        }
    };

    // Calculate summary statistics
    const totalCertificates = certificatePerCourse?.paging?.total || 0;

    const columns = [
        {
            name: "Sr No",
            selector: (row, index) => (page - 1) * limit + index + 1,
            sortable: false,
            width: "80px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Certificate No",
            selector: (row) => (
                <span className="text-primary fw-bold">
                    {row.certificate_no || "NA"}
                </span>
            ),
            sortable: true,
            minWidth: "180px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "User ID",
            selector: (row) => row.user_id || "NA",
            sortable: true,
            width: "100px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Name",
            selector: (row) => row.enroll_user.full_name || "NA",
            sortable: true,
            width: "180px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Purchase Date",
            selector: (row) => row.purchage_date ? moment(row.purchage_date).format("DD-MMM-YYYY HH:mm") : "NA",
            sortable: true,
            minWidth: "180px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Completion Date",
            selector: (row) => row.completion_date ? moment(row.completion_date).format("DD-MMM-YYYY HH:mm") : "NA",
            sortable: true,
            minWidth: "180px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Certificate",
            selector: (row) => (
                <div className="d-flex gap-1">
                    {row.certificate ? (
                        <>
                            <button 
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleViewCertificate(row)}
                                title="View Certificate"
                            >
                                <i className="fe-eye me-1"></i>
                                View
                            </button>
                        </>
                    ) : (
                        <span className="badge bg-secondary">NA</span>
                    )}
                </div>
            ),
            minWidth: "180px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Actions",
            selector: (row) => (
                <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
                    <button 
                        className="btn btn-sm btn-outline-info" 
                        onClick={() => handleViewUser(row)}
                        title="View User Details"
                    >
                        <i className="fe-user"></i>
                    </button>
                </div>
            ),
            minWidth: "100px",
            style: {
                fontSize: "14px",
                display: "flex",
                justifyContent: "center",
            },
        },
    ];

    // Custom styles for DataTable
    const customStyles = {
        headCells: {
            style: {
                justifyContent: "center",
                backgroundColor: "#f8f9fa",
                fontWeight: "bold",
            },
        },
        cells: {
            style: {
                justifyContent: "center",
            },
        },
    };

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
        setStartDate("");
        setEndDate("");
    };

    const handleCustomDateChange = () => {
        if (startDate && endDate && moment(startDate).isBefore(endDate)) {
            dispatch(
                certificatePerCourse_({
                    id: courseId,
                    period: "custom",
                    startDate: moment(startDate).format("YYYY-MM-DD"),
                    endDate: moment(endDate).format("YYYY-MM-DD"),
                    page,
                    limit,
                    search: searchTerm
                })
            );
        } else {
            Swal.fire({
                title: 'Invalid Dates',
                text: 'Please select valid start and end dates.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleSearch = useCallback((event) => {
        const value = event.target.value;
        setSearchTerm(value);
        setPage(1); // Reset to first page when searching
        
        // Debounce the search to avoid too many API calls
        const timeoutId = setTimeout(() => {
            dispatch(certificatePerCourse_({
                id: courseId,
                period: filter,
                startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                page: 1,
                limit,
                search: value
            }));
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [dispatch, courseId, filter, startDate, endDate, limit]);

    const handleExportData = useCallback(async () => {
        try {
            setIsExporting(true);

            // Fetch all data for export
            await dispatch(certificatePerCourse_({
                id: courseId,
                period: filter,
                startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                page: 1,
                is_report: 1,
                search: searchTerm
            }))
                .then((response) => {
                    if (response.payload && response.payload.data.data) {
                        // Map the data to the format we want for Excel
                        const exportData = response.payload.data.data.map((row, index) => ({
                            "Sr No": index + 1,
                            "Certificate No": row.certificate_no || "NA",
                            "User ID": row.user_id || "NA",
                            "User Name": row.enroll_user?.full_name || "NA",
                            "Course Language": row.user_course?.language || "NA",
                            "Course Duration": row.user_course?.duration || "NA",
                            "Course Price": `₹${parseFloat(row.user_course?.price || 0).toFixed(2)}`,
                            "Purchase Date": row.purchage_date
                                ? moment(row.purchage_date).format("DD-MMM-YYYY HH:mm:ss")
                                : "NA",
                            "Completion Date": row.completion_date
                                ? moment(row.completion_date).format("DD-MMM-YYYY HH:mm:ss")
                                : "Not Completed",
                            "Certificate Available": row.certificate ? "Yes" : "No",
                            "Certificate URL": row.certificate || "NA",
                            "Order ID": row.order_id || "NA",
                            "Created At": moment(row.createdAt).format("DD-MMM-YYYY HH:mm:ss"),
                        }));

                        // Create summary data
                        const summaryData = [
                            {
                                "Certificates Report Summary": "Course Certificates Overview",
                                "Value": ""
                            },
                            {
                                "Certificates Report Summary": "Total Certificates",
                                "Value": response.payload.data.data.length
                            },
                            {
                                "Certificates Report Summary": "Export Date",
                                "Value": moment().format("DD-MMM-YYYY HH:mm:ss")
                            }
                        ];

                        // Create workbook and worksheets
                        const workbook = XLSX.utils.book_new();

                        // Add summary sheet
                        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
                        XLSX.utils.book_append_sheet(workbook, summarySheet, "Certificates Summary");

                        // Add certificate data sheet
                        const certificateSheet = XLSX.utils.json_to_sheet(exportData);
                        XLSX.utils.book_append_sheet(workbook, certificateSheet, "Certificate Data");

                        // Save the file
                        const fileName = `Certificates_Report_${moment().format("YYYY-MM-DD")}.xlsx`;
                        XLSX.writeFile(workbook, fileName);

                        Swal.fire({
                            icon: "success",
                            title: "Export Successful",
                            text: `Data exported to ${fileName}`,
                        });

                        // Refresh current data
                        dispatch(certificatePerCourse_({ 
                            id: courseId, 
                            period: filter, 
                            startDate, 
                            endDate, 
                            page, 
                            limit, 
                            search: searchTerm 
                        }));
                    } else {
                        throw new Error("No data available for export");
                    }
                })
                .finally(() => {
                    setIsExporting(false);
                });
        } catch (error) {
            console.error("Export error:", error);
            Swal.fire({
                icon: "error",
                title: "Export Failed",
                text: error.message || "Unable to export certificate data",
            });
            setIsExporting(false);
        }
    }, [dispatch, courseId, filter, startDate, endDate, searchTerm, page, limit]);

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="">
            <div className="col-md-12 col-xl-12 mt-1">
                <div className="widget-rounded-circle card">
                    <div className="card-body">
                        {/* Header with Export Button */}
                        <div className="row mb-3">
                            <div className="col d-flex justify-content-end">
                                <button
                                    onClick={handleExportData}
                                    className="btn btn-outline-warning d-flex align-items-center gap-2"
                                    disabled={isExporting}
                                >
                                    {isExporting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fe-download me-1"></i>
                                            Export Report
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Filters and Search */}
                        <div className="row mb-3">
                            <div className="col-md-3">
                                <label className="form-label">Time Period</label>
                                <select className="form-select" onChange={handleFilterChange} value={filter}>
                                    <option value="">All</option>
                                    <option value="day">Today</option>
                                    <option value="week">Week</option>
                                    <option value="month">Month</option>
                                    <option value="year">Year</option>
                                    <option value="custom">Custom</option>
                                </select>
                            </div>
                            <div className="col-md-6">
                                {filter === "custom" && (
                                    <div className="row">
                                        <div className="col">
                                            <label className="form-label">Start Date</label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                selectsStart
                                                startDate={startDate}
                                                endDate={endDate}
                                                placeholderText="Start Date"
                                                className="form-control"
                                                dateFormat="dd-MMM-yyyy"
                                            />
                                        </div>
                                        <div className="col">
                                            <label className="form-label">End Date</label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                selectsEnd
                                                startDate={startDate}
                                                endDate={endDate}
                                                minDate={startDate}
                                                placeholderText="End Date"
                                                className="form-control"
                                                dateFormat="dd-MMM-yyyy"
                                            />
                                        </div>
                                        <div className="col-auto d-flex align-items-end">
                                            <button className="btn btn-primary" onClick={handleCustomDateChange}>
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Search</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Certificate No, User ID, User Name..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                    <span className="input-group-text">
                                        <i className="fe-search"></i>
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Summary Cards */}
                        <div className="row mb-4">
                            <div className="col-md-3">
                                <div className="card bg-primary text-white">
                                    <div className="card-body">
                                        <h6 className="card-title text-white">Total Certificates</h6>
                                        <h3 className="mt-2 text-white">{totalCertificates}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Data Table */}
                        <div className="row">
                            <div className="col-12">
                                <DataTable
                                    title={null}
                                    columns={columns}
                                    data={certificatePerCourse?.data || []}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={certificatePerCourse?.paging?.total}
                                    paginationDefaultPage={page}
                                    paginationPerPage={limit}
                                    paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                                    onChangeRowsPerPage={(newrow) => setLimit(newrow)}
                                    onChangePage={(newPage) => setPage(newPage)}
                                    responsive
                                    selectableRowsHighlight
                                    highlightOnHover
                                    striped
                                    fixedHeader
                                    persistTableHead
                                    noDataComponent={
                                        <div className="text-center p-4">
                                            <h5 className="text-muted">No Certificates Found</h5>
                                            <p className="text-muted">No certificates available for the selected criteria.</p>
                                        </div>
                                    }
                                    customStyles={customStyles}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UsersCertificates;